import { useEffect, useState } from 'react';

import { ErroreApi } from '../api/client.ts';
import { scadenze as apiScadenze } from '../api/scadenziario.ts';
import type { Scadenza } from '../api/tipi-dati.ts';
import { Dialogo, RiepilogoScadenza } from './Dialogo.tsx';
import { dataBreve } from './formato.ts';

/**
 * Riapertura di una scadenza completata per errore.
 *
 * Cancella il rinnovo che era stato generato, quindi va chiesta conferma
 * dicendo **quale** riga sparirà: è l'unica cancellazione fisica di tutta
 * l'applicazione, e l'utente deve poterla riconoscere prima di confermarla.
 */
export function DialogoRiapertura({
  scadenza,
  onChiudi,
  onRiaperta,
}: {
  scadenza: Scadenza;
  onChiudi: () => void;
  onRiaperta: (messaggio: string) => void;
}) {
  const [rinnovo, setRinnovo] = useState<Scadenza | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [inCaricamento, setInCaricamento] = useState(true);

  /*
   * Si cerca il rinnovo generato per poterlo nominare nella conferma.
   *
   * ⚠️ La ricerca passa dal filtro per soggetto, e **una scadenza orfana in
   * quel filtro non ci finisce**: il suo `soggetto_id` è nullo, quindi il
   * backend non la restituirebbe mai. Non è un caso da forzare — cercare il
   * rinnovo scandendo l'intera tabella per trovare un `scadenzaPrecedenteId`
   * significherebbe scaricare tutto lo storico dell'azienda ogni volta che si
   * apre questo dialogo, e per un caso raro.
   *
   * Si preferisce dirlo: sotto compare che non si è potuto verificare in
   * anticipo. La riapertura resta corretta comunque — è il backend a
   * cancellare il rinnovo, e a dire nell'esito quale ha cancellato.
   */
  const soggettoId = scadenza.soggetto.id;

  useEffect(() => {
    if (soggettoId === null) { setInCaricamento(false); return; }

    let annullato = false;

    apiScadenze
      .elenca({ soggettoId, limite: 200 })
      .then((r) => {
        if (annullato) return;
        setRinnovo(r.elementi.find((s) => s.scadenzaPrecedenteId === scadenza.id) ?? null);
      })
      .catch(() => undefined)
      .finally(() => { if (!annullato) setInCaricamento(false); });

    return () => { annullato = true; };
  }, [scadenza.id, soggettoId]);

  async function conferma() {
    setErrore(null);
    setInCorso(true);

    try {
      const esito = await apiScadenze.riapri(scadenza.id);
      onRiaperta(
        esito.rinnovoRimosso === null
          ? `«${scadenza.tipo.nome}» riaperta.`
          : `«${scadenza.tipo.nome}» riaperta. Cancellato il rinnovo del ${dataBreve(esito.rinnovoRimosso.dataScadenza)}.`,
      );
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
      setInCorso(false);
    }
  }

  return (
    <Dialogo titolo="Riapri la scadenza" aperto onChiudi={onChiudi}>
      <RiepilogoScadenza
        soggetto={scadenza.soggetto.denominazione}
        soggettoEliminato={scadenza.soggetto.eliminato}
        codice={scadenza.soggetto.codice}
        reparto={scadenza.soggetto.reparto}
        tipo={scadenza.tipo.nome}
        tipoEliminato={scadenza.tipo.eliminato}
        dataPrevista={dataBreve(scadenza.dataScadenza)}
        giorni={scadenza.giorniMancanti}
      />

      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <p className="rinnovo-testo">
        La scadenza torna <strong>APERTA</strong> e il completamento
        {scadenza.dataCompletamento !== null && ` del ${dataBreve(scadenza.dataCompletamento)}`}
        {' '}viene annullato.
      </p>

      {inCaricamento ? (
        <p className="aiuto">Verifica del rinnovo generato…</p>
      ) : soggettoId === null ? (
        /* Onestà invece di silenzio: dire «non risulta nessun rinnovo» sarebbe
           una risposta che non abbiamo. */
        <div className="avviso-attenzione" role="note">
          <strong>Non è stato possibile verificare in anticipo se esiste un rinnovo.</strong>
          <p className="aiuto">
            Il soggetto di questa scadenza è stato eliminato, e la ricerca del rinnovo passa
            proprio da lì. Se un rinnovo esiste verrà cancellato lo stesso, e comparirà nel
            messaggio di conferma appena l'operazione è finita.
          </p>
        </div>
      ) : rinnovo !== null ? (
        <div className="avviso-attenzione" role="note">
          <strong>Verrà cancellata anche la scadenza del {dataBreve(rinnovo.dataScadenza)}</strong>,
          creata automaticamente al momento del completamento.
          <p className="aiuto">
            È l'unica cancellazione definitiva dell'applicazione, ed è corretta solo perché
            quella riga non è mai stata una scadenza reale: è nata dall'errore che stiamo
            correggendo. Se nel frattempo qualcuno ci ha lavorato sopra, l'operazione viene
            rifiutata.
          </p>
        </div>
      ) : (
        <p className="aiuto blocco-nota">
          Non risulta nessun rinnovo da cancellare.
        </p>
      )}

      <div className="dialogo-azioni">
        <button
          type="button"
          className="bottone bottone-principale"
          onClick={() => void conferma()}
          disabled={inCorso || inCaricamento}
        >
          {inCorso ? 'Riapertura…' : 'Riapri la scadenza'}
        </button>
        <button type="button" className="bottone-piatto" onClick={onChiudi} disabled={inCorso}>
          Torna indietro
        </button>
      </div>
    </Dialogo>
  );
}
